import React, { memo, useState, useDeferredValue } from 'react';
import { ChevronDown } from 'lucide-react';
import styles from '../../styles/ChatApp.module.css';
import MarkdownRenderer from './MarkdownRenderer';
import { parseThinkingContent } from '../common/thinkingParser';

/**
 * A specialized component for rendering the currently streaming message.
 * This is separate from ChatMessage to optimize re-rendering.
 * Only this component re-renders during streaming, not the entire message list.
 *
 * useDeferredValue lets React deprioritize the expensive markdown re-parse
 * while still keeping the streaming cursor in sync with every new chunk.
 */
const StreamingMessage = memo(({ content, meta }) => {
    const { modelUsed } = meta;
    const deferredContent = useDeferredValue(content);

    const { content: visibleContent, thinkingBlocks, isThinking } = parseThinkingContent(deferredContent);
    const [thinkingOpen, setThinkingOpen] = useState(false);

    const hasCompletedThinking = thinkingBlocks.length > 0;

    return (
        <div className={`${styles.messageWrapper} ${styles.assistant}`}>
            {modelUsed && (
                <span className={styles.modelInfo}>Answer from: {modelUsed}</span>
            )}

            <div className={`${styles.messageBubble} ${styles.assistantBubble}`}>
                {/* Show a spinner while the model is actively thinking */}
                {isThinking && (
                    <div className={styles.thinkingBlock}>
                        <div className={styles.thinkingSpinner}>
                            <span className={styles.thinkingDot} />
                            <span className={styles.thinkingDot} />
                            <span className={styles.thinkingDot} />
                            <span className={styles.thinkingSpinnerLabel}>Thinking…</span>
                        </div>
                    </div>
                )}

                {/* Completed thinking blocks become a collapsible section */}
                {hasCompletedThinking && (
                    <div className={styles.thinkingBlock}>
                        <button
                            type="button"
                            className={styles.thinkingToggle}
                            onClick={() => setThinkingOpen(prev => !prev)}
                            aria-expanded={thinkingOpen}
                        >
                            <ChevronDown
                                size={13}
                                className={`${styles.thinkingChevron} ${thinkingOpen ? styles.thinkingChevronOpen : ''}`}
                            />
                            <span>Thinking</span>
                        </button>
                        {thinkingOpen && (
                            <div className={styles.thinkingContent}>
                                {thinkingBlocks.map((block, i) => (
                                    <MarkdownRenderer key={i} content={block} />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <MarkdownRenderer content={visibleContent} />
                <span className={styles.streamingCursor}></span>
            </div>
        </div>
    );
});

StreamingMessage.displayName = 'StreamingMessage';

export default StreamingMessage;